/**
 * dailyReviewBadge — local, pure tracking of how many cards the user reviewed
 * today, capped at 30 (DAILY_REVIEW_BADGE_THRESHOLD). Mirrors iOS
 * `dailyReviewBadge.ts` (`recordDailyReviewBadgeCompletions`,
 * `markFirstReviewCompleted`). localStorage-backed; no server dependency.
 */
import { DAILY_REVIEW_BADGE_THRESHOLD } from "./reviewConstants";

const COUNT_KEY = "dailyReviewBadgeCount";
const DATE_KEY = "dailyReviewBadgeDate";
const FIRST_REVIEW_KEY = "hasCompletedFirstReview";

// Local-calendar day key (YYYY-MM-DD). Mirrors iOS getLocalDateKey — uses the
// device's local Y/M/D, NOT UTC, so the daily-30 cap resets at local midnight.
function todayKey(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function readToday(): number {
  try {
    if (localStorage.getItem(DATE_KEY) !== todayKey()) return 0;
    return Number(localStorage.getItem(COUNT_KEY)) || 0;
  } catch {
    return 0;
  }
}

/** Add `n` reviewed cards to today's count (capped at the daily threshold). */
export function recordDailyReviewBadgeCompletions(n: number): void {
  try {
    const next = Math.min(readToday() + n, DAILY_REVIEW_BADGE_THRESHOLD);
    localStorage.setItem(DATE_KEY, todayKey());
    localStorage.setItem(COUNT_KEY, String(next));
  } catch {
    /* storage unavailable — non-fatal */
  }
}

/** How many cards reviewed today (0..threshold). */
export function getDailyReviewBadgeCount(): number {
  return readToday();
}

/** Mark that the user has finished at least one review session ever. */
export function markFirstReviewCompleted(): void {
  try {
    localStorage.setItem(FIRST_REVIEW_KEY, "true");
  } catch {
    /* non-fatal */
  }
}
